// src/components/pages/dialogs/PaymentTypeDialog.tsx

'use client';

import { useState, useEffect } from 'react';
import { DialogFooter } from '@/components/ui/dialog';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';

import { completarCita } from '@/components/hooks/useCitas';
import { useBonos } from '@/components/hooks/useBonos';

export type PaymentTypeDialogProps = {
  idCita: string;
  emailCliente: string;
  idServicio: string;
  tipoPago: string;
  setTipoPago: (value: string) => void;
  onCancel: () => void;
  onCloseAll: () => void;
  incrementReload: () => void;
  precio?: number;
};

const basePaymentOptions = [
  { value: 'tarjeta', label: 'Tarjeta' },
  { value: 'efectivo', label: 'Efectivo' },
  { value: 'bizum', label: 'Bizum' },
  { value: 'otros', label: 'Otros' },
];

export default function PaymentTypeDialog({
  idCita,
  emailCliente,
  idServicio,
  tipoPago,
  setTipoPago,
  onCancel,
  onCloseAll,
  incrementReload,
  precio,
}: PaymentTypeDialogProps) {
  const empresaId = localStorage.getItem('id_empresa') || '';

  /* ============================================================
     BONOS — modo `'vendidos'` para obtener getBonoDisponible
  ============================================================ */
  const { getBonoDisponible } = useBonos(empresaId, {
    tipo: 'vendidos' as const,
  });

  /* ============================================================
     ESTADOS
  ============================================================ */
  const [loading, setLoading] = useState(false);
  const [paymentOptions, setPaymentOptions] = useState(basePaymentOptions);
  const [bonoDisponible, setBonoDisponible] = useState<any | null>(null);
  const [usarBono, setUsarBono] = useState(false);
  const [prevTipoPago, setPrevTipoPago] = useState('tarjeta');

  /* ============================================================
     DETECTAR BONO DISPONIBLE
  ============================================================ */
  useEffect(() => {
    const loadBono = async () => {
      if (!emailCliente || !empresaId) return;

      const bono = await getBonoDisponible(emailCliente, idServicio, empresaId);
      if (!bono) {
        setBonoDisponible(null);
        return;
      }

      setBonoDisponible(bono);

      // añadir opción bono
      setPaymentOptions((opts) =>
        opts.some((o) => o.value === 'bono')
          ? opts
          : [...opts, { value: 'bono', label: 'Bono' }]
      );
    };

    loadBono();
  }, [emailCliente, idServicio, empresaId, getBonoDisponible]);

  /* ============================================================
     CONFIRMAR
  ============================================================ */
  const handleConfirm = async () => {
    if (loading) return;
    setLoading(true);

    try {
      const precioFinal = tipoPago === 'bono' ? 0 : precio ?? 0;

      const resp = await completarCita(
        idCita,
        tipoPago,
        precioFinal,
        tipoPago === 'bono' ? bonoDisponible : null
      );

      if (!resp.ok) throw resp.error;

      toast.success('Pago registrado correctamente');
      onCloseAll();
      incrementReload();
    } catch (err) {
      console.error(err);
      toast.error('No se pudo procesar el pago');
    } finally {
      setLoading(false);
    }
  };

  /* ============================================================
     UI
  ============================================================ */
  return (
    <>
      {/* SELECTOR */}
      <div className="bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-xl p-3 mt-1 space-y-2 shadow-sm">
        <Select
          value={tipoPago}
          onValueChange={(value) => {
            setTipoPago(value);
            if (value === 'bono') {
              setPrevTipoPago(tipoPago);
              setUsarBono(true);
            } else {
              setUsarBono(false);
            }
          }}
        >
          <SelectTrigger className="w-full h-9 text-sm focus:ring-[#103254]/30 dark:bg-white/5 dark:border-white/10 dark:text-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="z-[70]">
            {paymentOptions.map((option) => (
              <SelectItem
                key={option.value}
                value={option.value}
                className="text-sm"
              >
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {precio != null && (
          <div className="pt-4">
            <p className="text-sm font-semibold text-gray-900 dark:text-white">
              Precio:
              <span className="ml-2 font-bold text-[#103254] dark:text-blue-300">
                {tipoPago === 'bono' ? '0 €' : `${precio.toFixed(2)} €`}
              </span>
            </p>
          </div>
        )}
      </div>

      {/* MÓDULO BONO */}
      {bonoDisponible && (
        <div className="mt-4 p-3 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-300 dark:border-green-800 shadow-sm space-y-1">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-green-700 dark:text-green-300">
              Bono disponible
            </p>

            <Switch
              checked={usarBono}
              onCheckedChange={(checked) => {
                setUsarBono(checked);

                if (checked) {
                  setPrevTipoPago(tipoPago);
                  setTipoPago('bono');
                } else {
                  setTipoPago(prevTipoPago);
                }
              }}
            />
          </div>

          <p className="text-xs text-gray-600 dark:text-gray-400">
            Vence el{' '}
            {new Date(bonoDisponible.fecha_vencimiento).toLocaleDateString()}
          </p>

          <p className="text-xs text-gray-700 dark:text-gray-300">
            Usos:{' '}
            {bonoDisponible.cantidad_servicios -
              bonoDisponible.usos_restantes +
              (usarBono ? 1 : 0)}
            /{bonoDisponible.cantidad_servicios} (
            {bonoDisponible.usos_restantes - (usarBono ? 1 : 0)} restantes)
          </p>
        </div>
      )}

      {/* BOTONES */}
      <DialogFooter className="mt-6 flex gap-3 flex-col sm:flex-row">
        <Button
          onClick={onCancel}
          disabled={loading}
          variant="outline"
          className="rounded-lg font-medium w-full bg-transparent dark:bg-transparent border-gray-300 dark:border-white/10 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
        >
          Volver
        </Button>

        <Button
          onClick={handleConfirm}
          disabled={loading || !tipoPago}
          className="
            rounded-xl text-white font-semibold text-base shadow-md py-3
            bg-green-600 hover:bg-green-700 dark:bg-green-600 dark:hover:bg-green-700
            disabled:opacity-50 w-full transition-colors
          "
        >
          {loading ? 'Procesando…' : 'Confirmar'}
        </Button>
      </DialogFooter>
    </>
  );
}